import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clapperboard } from "lucide-react";

const chapters = [
  { t: 0, label: "Arrival · DIP Phase 2" },
  { t: 14, label: "Kitchen Units · Blank Canvas" },
  { t: 22, label: "Fire-Rated Corridor" },
  { t: 38, label: "Hot Kitchen · Extraction" },
  { t: 46, label: "Wash & Prep Stations" },
  { t: 54, label: "Combi Station · Cookline" },
];

export default function VideoTour({ open, onClose }) {
  const videoRef = useRef(null);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    if (open) {
      document.body.style.overflow = "hidden";
      if (window.__lenis) window.__lenis.stop();
      window.addEventListener("keydown", esc);
    }
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
      window.removeEventListener("keydown", esc);
    };
  }, [open, onClose]);

  const onTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress(v.currentTime / v.duration);
    let idx = 0;
    chapters.forEach((c, i) => {
      if (v.currentTime >= c.t - 0.4) idx = i;
    });
    setActive(idx);
  };

  const seek = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    v.play().catch(() => {});
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-md"
          onClick={onClose}
          data-testid="video-tour-overlay"
        >
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 md:px-8">
            <p className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold md:text-xs">
              <Clapperboard size={15} /> Facility Walkthrough · Dubai Investment Park
            </p>
            <button
              onClick={onClose}
              data-testid="video-tour-close"
              aria-label="Close video tour"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold transition-all duration-300 hover:bg-gold hover:text-black"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-1 items-center overflow-y-auto px-5 pb-8 md:px-8">
            <motion.div
              initial={{ scale: 0.94, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 12, opacity: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto w-full max-w-6xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative overflow-hidden rounded-xl border border-gold/30 shadow-[0_60px_120px_-30px_rgba(0,0,0,1)]">
                <video
                  ref={videoRef}
                  controls
                  autoPlay
                  playsInline
                  onTimeUpdate={onTime}
                  className="aspect-video w-full bg-black"
                  data-testid="video-tour-player"
                >
                  <source src="/assets/video/facility-tour.webm" type="video/webm" />
                  <source src="/assets/video/facility-tour.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-x-0 top-0 h-1 bg-white/10">
                  <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${progress * 100}%` }} data-testid="video-tour-progress" />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="font-serif text-xl italic text-gold-light md:text-2xl" data-testid="video-tour-active-chapter">
                  {chapters[active].label}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">
                  Chapter {String(active + 1).padStart(2, "0")} / {String(chapters.length).padStart(2, "0")}
                </p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2.5" data-testid="video-tour-chapters">
                {chapters.map((c, i) => (
                  <button
                    key={c.label}
                    onClick={() => seek(c.t)}
                    data-testid={`video-tour-chapter-${i}`}
                    className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 md:text-[11px] ${
                      i === active
                        ? "border-gold bg-gold text-black"
                        : "border-gold/30 text-gold/80 hover:border-gold/70 hover:text-gold"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
